import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Output,
} from '@angular/core';
import { BookingService } from '../../services/resources/booking.service';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  Subscription,
  map,
  tap,
  take,
  switchMap,
  of,
} from 'rxjs';
import { Booking, FilterState } from '../../core/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardFilterService } from '../../services/filter/dashboard-filter.service';
import { parseISO } from 'date-fns';
import { dashboardQuicksearch } from '../../core/quicksearch.util';

@Component({
  selector: 'app-booking-search',
  imports: [CommonModule, ReactiveFormsModule],
  standalone: true,
  template: `
    <div class="relative w-full" [formGroup]="searchForm">
      <input
        formControlName="searchControl"
        id="searchQuery"
        type="text"
        name="searchQuery"
        autoComplete="off"
        (focus)="onActive()"
        class="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-columbus-blue-500 focus:border-columbus-blue-500"
        placeholder="Search..."
      />
      <div
        *ngIf="isSearching"
        class="absolute  right-1 top-1/2 -translate-y-1/2 items-center "
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="size-6 my-2 animate-spin fill-columbus-blue-600"
          viewBox="0 0 24 24"
        >
          <path
            d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
            opacity=".25"
          />
          <path
            d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
          ></path>
        </svg>
      </div>
    </div>
  `,
  styles: ``,
})
export class BookingSearchComponent {
  @Output() searchBookings = new EventEmitter<{
    bookings: Booking[];
    searchTerm: string;
  }>();
  private readonly destroyRef = inject(DestroyRef);
  bookingService = inject(BookingService);
  filterService = inject(DashboardFilterService);

  nfb = inject(NonNullableFormBuilder);
  bookingsSubscription: Subscription | undefined = undefined;
  bookings: Booking[] = [];
  savedDate: Date | undefined = undefined;

  filteredBookings: Booking[] = [];
  searchQuery = '';
  isSearching = false;

  debounceMS = 500;

  searchForm = this.nfb.group({
    searchControl: [''],
  });

  cachedSource: {
    date: Date | undefined;
    bookings: Booking[];
  } = {
    date: undefined,
    bookings: [],
  };

  constructor() {
    this.searchForm
      .get('searchControl')
      ?.valueChanges.pipe(
        tap(() => (this.isSearching = true)),
        map((value) => value.trim()),
        distinctUntilChanged(),
        debounceTime(this.debounceMS),
        tap(() => (this.isSearching = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((searchTerm) => {
        // console.log('SearchTerm', searchTerm);
        // console.log('Bookings', this.bookings);
        this.filterService.updateSearchTerm(searchTerm);
        if (!this.bookings || searchTerm === '') {
          this.searchBookings.emit({ bookings: [], searchTerm: '' });
          return;
        }
        const filteredBookings = this.filterBookingsBySearch(
          this.bookings,
          searchTerm,
        );

        console.log('filtered', filteredBookings);

        //        console.log('Emitting filter!', filteredBookings);
        this.searchBookings.emit({
          bookings: filteredBookings,
          searchTerm: searchTerm,
        });
      });
  }

  resetSearch() {
    this.searchForm.reset();
    this.isSearching = false;
  }

  searchTermValue(): string {
    const searchTerm = this.searchForm.get('searchControl')?.getRawValue();
    if (!searchTerm) {
      return '';
    } else {
      return searchTerm as string;
    }
  }

  onActive() {
    this.filterService.filters$
      .pipe(
        take(1),
        switchMap((fs: FilterState) => {
          const fsDate = parseISO(fs.savedDate as string);

          this.savedDate = fsDate;
          const biggerSource =
            fs.loadedBookings.length < this.cachedSource.bookings.length;

          if (
            this.cachedSource.date?.getTime() === fsDate.getTime() &&
            biggerSource
          ) {
            return of(this.cachedSource.bookings).pipe(first()); // Just return, don't set this.bookings here
          } else {
            this.savedDate = fsDate;
            return this.bookingService.getAllBookingsForCW(fsDate).pipe(
              first(),
              tap((bookings) => {
                if (bookings) {
                  this.filterService.updateloadedBookings(bookings);
                  this.filterService.updateSearchTerm(this.searchTermValue());
                  this.cachedSource.date = fsDate;
                  this.cachedSource.bookings = bookings;
                }
              }),
            );
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((bookings) => {
        if (bookings) {
          this.bookings = bookings;
        }
      });
  }

  filterBookingsBySearchOld(bookings: Booking[], term: string): Booking[] {
    if (!term || term.trim() === '') {
      return [...bookings]; // Return all bookings if no search term
    }
    const searchTerm = term.toLowerCase().trim();

    const filterPredicate = (item: Booking): boolean => {
      if (!item) return false;
      const simpleStringMatch =
        String(item.cruiseDetails?.invoiceNumber)
          ?.toLowerCase()
          .includes(searchTerm) ||
        item.duration?.tourCode?.toLowerCase().includes(searchTerm);

      if (simpleStringMatch) return true;

      if (item.guides) {
        const guidesMatch = Object.values(item.guides).some((guide) => {
          if (guide.other) {
            const otherMatch = Object.values(guide.other).some((otherVal) => {
              return otherVal?.toLowerCase().includes(searchTerm);
            });

            if (otherMatch) return true;
          }

          return (
            guide.guideDetails?.forename?.toLowerCase().includes(searchTerm) ||
            guide.guideDetails?.surname?.toLowerCase().includes(searchTerm)
          );
        });

        if (guidesMatch) return true;
      }

      return false;
    };

    return bookings.filter(filterPredicate);
  }

  filterBookingsBySearch(bookings: Booking[], term: string): Booking[] {
    return dashboardQuicksearch(bookings, term);
  }
}
